// Form Builder Types — exported from shared/ so both client and server can import.
// The FormSchema JSON is stored as text in booking_forms.form_schema_json.

export type FieldKind =
  // Basic
  | "text" | "textarea" | "email" | "phone" | "number"
  // Choice
  | "dropdown" | "radio" | "checkbox" | "multi_select"
  // Date/Time
  | "date_picker" | "calendar_booking_slot"
  // Special
  | "event_offerings_picker" | "availability_calendar"
  // Layout
  | "heading" | "divider" | "image" | "rich_text"
  // Advanced
  | "file_upload" | "signature" | "rating";

export interface FieldOption {
  id: string;
  label: string;
  value: string;
}

export interface ConditionalRule {
  id: string;
  /** Field ID on this page whose value triggers the rule */
  fieldId: string;
  operator: "equals" | "not_equals" | "contains" | "is_before" | "is_after" | "is_empty" | "is_not_empty";
  value: string;
  /** Page index to jump to when the condition matches */
  targetPageIndex: number;
}

export interface FormField {
  id: string;
  kind: FieldKind;
  label: string;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  // Basic field options
  minLength?: number;
  maxLength?: number;
  // Choice field options
  options?: FieldOption[];
  allowOther?: boolean;
  // Date/Time options
  minAdvanceDays?: number;
  maxAdvanceDays?: number;
  // Event Offerings picker
  showPrice?: boolean;
  showDuration?: boolean;
  // Availability Calendar
  availabilityMode?: "master_schedule" | "connected_calendar";
  bufferBefore?: number;
  bufferAfter?: number;
  // Layout: image url
  imageUrl?: string;
  // Layout: rich text HTML
  richTextHtml?: string;
  // Layout: heading level
  headingLevel?: 1 | 2 | 3;
  // Rating
  ratingMax?: number;
  // Internal system binding (for pre-wired fields)
  systemBinding?: "first_name" | "last_name" | "email" | "phone" | "organization" | "city_state";
}

export interface FormPage {
  id: string;
  title: string;
  fields: FormField[];
  /** Conditional routing rules; evaluated in order; first match wins */
  conditionalRules?: ConditionalRule[];
  /** Default next page index (null = last page / submit) */
  defaultNextPageIndex?: number | null;
}

export interface FormTheme {
  primaryColor?: string;
  backgroundColor?: string;
  fontFamily?: "system" | "inter" | "poppins" | "playfair";
  borderRadius?: "sharp" | "rounded" | "pill";
  logoUrl?: string;
}

export interface FormSchema {
  version: 1;
  pages: FormPage[];
  theme?: FormTheme;
}

// ── Default schema seed ────────────────────────────────────────────────────
// Matches the existing two-step live form: Step 1 contact info, Step 2 event details.

function makeId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function createDefaultFormSchema(): FormSchema {
  return {
    version: 1,
    pages: [
      {
        id: makeId(),
        title: "Your Information",
        fields: [
          { id: makeId(), kind: "text", label: "First Name", required: true, systemBinding: "first_name", placeholder: "Jane" },
          { id: makeId(), kind: "text", label: "Last Name", required: true, systemBinding: "last_name", placeholder: "Smith" },
          { id: makeId(), kind: "email", label: "Email Address", required: true, systemBinding: "email", placeholder: "jane@example.com" },
          { id: makeId(), kind: "phone", label: "Phone Number", required: false, systemBinding: "phone", placeholder: "(555) 555-5555" },
          { id: makeId(), kind: "text", label: "Organization / Company", required: false, systemBinding: "organization", placeholder: "Acme Corp" },
        ],
      },
      {
        id: makeId(),
        title: "Event Details",
        fields: [
          { id: makeId(), kind: "event_offerings_picker", label: "Event Package", required: true, showPrice: true, showDuration: true },
          { id: makeId(), kind: "availability_calendar", label: "Preferred Date & Time", required: false, availabilityMode: "master_schedule" },
        ],
      },
    ],
    theme: { primaryColor: "#2563eb", fontFamily: "inter", borderRadius: "rounded" },
  };
}
